import { useCallback, useEffect, useState } from 'react';
import { apiUtils } from '../../../utils/apiUtils';
import { Button } from '../../ui';
import { canDeleteClientData } from '../../../utils/permissions';
import { useUser } from '../../../contexts/UserContext';
import { DELETION_STATUS_REFRESH } from './deletionEvents';

export function DeletionStatusPanel() {
  const { user } = useUser();
  const allowed = canDeleteClientData(user);
  const [open, setOpen] = useState([]);
  const [recent, setRecent] = useState([]);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const refresh = useCallback(async () => {
    if (!allowed) return;
    try {
      const [activeResponse, recentResponse] = await Promise.all([
        apiUtils.get('/api/deletions?state=open'),
        apiUtils.get('/api/deletions?state=recent'),
      ]);
      const [active, finished] = await Promise.all([activeResponse.json(), recentResponse.json()]);
      if (!active.success || !finished.success) throw new Error('Could not load deletion status');
      setOpen(active.data || []);
      setRecent(finished.data || []);
      setError('');
    } catch (cause) {
      setError(cause.response?.data?.error || cause.message || 'Could not load deletion status');
    }
  }, [allowed]);

  useEffect(() => {
    if (!allowed) return undefined;
    refresh();
    const timer = setInterval(refresh, 5000);
    window.addEventListener(DELETION_STATUS_REFRESH, refresh);
    return () => { clearInterval(timer); window.removeEventListener(DELETION_STATUS_REFRESH, refresh); };
  }, [allowed, refresh]);

  if (!allowed || (!open.length && !recent.length && !error)) return null;

  const retry = async (id) => {
    setBusyId(id);
    try {
      await apiUtils.post(`/api/deletions/${id}/retry`, {});
      await refresh();
    } catch (cause) {
      setError(cause.response?.data?.error || cause.message || 'Could not retry deletion');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section id="deletion-status" aria-label="Deletion status" className="mb-5 rounded-xl border border-border bg-card p-4 sm:p-5">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-semibold text-foreground">Deletion status</h2>
        <Button variant="outline" size="sm" onClick={refresh}>Refresh status</Button>
      </div>
      {error && <p role="alert" className="mt-2 text-sm text-destructive">{error}</p>}
      <div className="mt-3 space-y-3" aria-live="polite">
        {[...open, ...recent].map((operation) => (
          <div key={operation.id} className="flex flex-col gap-2 border-t border-border pt-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="font-medium text-sm text-foreground">{operation.targetType === 'client' ? 'Client' : 'Account'} deletion: {operation.state === 'completed' ? 'Completed' : operation.state === 'needs_attention' ? 'Needs attention' : operation.state === 'preparing' ? 'Preparing' : 'Deleting'}</p>
              <p className="text-sm text-muted-foreground">{operation.message}</p>
              <p className="text-xs text-muted-foreground">Started {new Date(operation.createdAt).toLocaleString()} · Reference {String(operation.id).slice(-8)}</p>
              {operation.state === 'completed' && <p className="text-xs text-muted-foreground">{operation.accountsRemoved} accounts and {operation.scansRemoved} scans removed</p>}
            </div>
            {operation.state === 'needs_attention' && <Button variant="outline" size="sm" disabled={busyId === operation.id} onClick={() => retry(operation.id)}>{busyId === operation.id ? 'Retrying...' : 'Retry deletion'}</Button>}
          </div>
        ))}
      </div>
    </section>
  );
}
